import {
  FC,
  useState,
  useMemo,
  useEffect,
  useRef,
  type ChangeEvent,
} from "react";
import { map, get } from "lodash";
import CompanyLogo from "./CompanyLogo";
import { useSearchCompanies } from "@lhd/hooks";
import Loading from "@lhd/components/Loading";
import { IoClose } from "react-icons/io5";
import { Spin } from "antd";
import {
  SearchPopupWrapper,
  SearchInputWrapper,
  CompanyList,
  StyledInput,
  CompanyItem,
  CompanyName,
  EmptyState,
  SearchIcon,
} from "./styled";

interface SearchPopupProps {
  isOpen: boolean;
  companies: any[];
  onSelectCompany: (company: any) => void;
  onClose: () => void;
}

const SearchPopup: FC<SearchPopupProps> = ({
  isOpen,
  companies,
  onSelectCompany,
  onClose,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSearchValue("");
      setDebouncedSearchValue("");
      setIsDebouncing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedValue = searchValue.trim();

    if (!trimmedValue) {
      setDebouncedSearchValue("");
      setIsDebouncing(false);
      return;
    }

    setIsDebouncing(true);

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchValue(trimmedValue);
      setIsDebouncing(false);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchValue]);

  const { data: searchData, isLoading: isSearching } =
    useSearchCompanies(debouncedSearchValue);

  const filteredCompanies = useMemo(() => {
    const hasSearchKeyword =
      debouncedSearchValue && debouncedSearchValue.length > 0;

    if (!hasSearchKeyword) {
      return companies || [];
    }

    if (searchData?.data) {
      return map(searchData.data, (company) => ({
        name: get(company, "name", ""),
        logo: company.logo_original_url,
        isWorkDefault: company.is_work_default,
        id: get(company, "id"),
      }));
    }
    return [];
  }, [companies, searchData, debouncedSearchValue]);

  const handleSelectCompany = (company: any) => {
    onSelectCompany(company);
    setSearchValue("");
    onClose();
  };

  if (!isOpen) return null;

  const shouldShowLoadingOverlay =
    (isDebouncing && !!searchValue.trim()) ||
    (isSearching && !!debouncedSearchValue);

  return (
    <SearchPopupWrapper className="search-popup-wrapper">
      <SearchInputWrapper>
        <StyledInput
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e.target.value)
          }
          placeholder="検索したい企業をご入力ください。"
          autoFocus
          allowClear
          suffix={<SearchIcon />}
        />

        <div className="close-button">
          <IoClose size={24} onClick={onClose} />
        </div>
      </SearchInputWrapper>
      <CompanyList>
        {shouldShowLoadingOverlay ? (
          <Loading
            isOverlay
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              borderRadius: 6,
              backgroundColor: "rgba(1, 8, 23, 0.16)",
              color: "#000E96",
            }}
          />
        ) : null}
        {filteredCompanies.length > 0 ? (
          filteredCompanies.map((company: any) => (
            <CompanyItem
              key={company.id}
              onClick={() => handleSelectCompany(company)}
            >
              <CompanyLogo company={company} />
              <CompanyName>{company.name}</CompanyName>
            </CompanyItem>
          ))
        ) : (
          <EmptyState>
            {debouncedSearchValue
              ? "検索結果が見つかりません"
              : "会社が見つかりません"}
          </EmptyState>
        )}
      </CompanyList>
    </SearchPopupWrapper>
  );
};

export default SearchPopup;
